import { type ReactNode, useEffect, useRef, useState } from 'react';
import { IconChevronDown, IconChevronUp } from '@optiaxiom/icons';
import { Box, Button, Flex, theme } from '@optiaxiom/react';

type Props = {
  children: ReactNode;
  /**
   * Collapsed height cap in px. Content taller than this is cropped with a
   * soft fade; shorter content renders untouched. Default tuned so a long
   * card previews its title + a few rows before the fold.
   */
  maxHeight?: number;
};

/**
 * Crops a tall card body to `maxHeight` with a bottom fade and a "Show more"
 * toggle. Click-to-expand (deliberately NOT hover): it's touch-friendly,
 * doesn't fire on accidental pointer passes, and doesn't fight interactive
 * content inside the card (e.g. Proteus checkboxes) the way a hover-expand
 * would. Capping the tallest card also evens out the grid row, so the board
 * stops leaving a sea of whitespace under the shorter cards.
 *
 * Short bodies get no fade and no button — the affordance only appears when
 * the content actually overflows the cap.
 */
export function CollapsibleBody({ children, maxHeight = 220 }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The cap is on the wrapper, so the inner content's natural height is
    // scrollHeight. A small epsilon avoids a toggle for a one-pixel overflow.
    const check = () => setOverflowing(el.scrollHeight > maxHeight + 8);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [maxHeight]);

  const collapsed = overflowing && !expanded;

  return (
    <Box style={{ position: 'relative' }}>
      <Box ref={ref} style={{ maxHeight: collapsed ? maxHeight : undefined, overflow: 'hidden' }}>
        {children}
      </Box>
      {collapsed && (
        <Box
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 48,
            pointerEvents: 'none',
            background: `linear-gradient(to bottom, transparent, ${theme.colors['bg.default']})`,
          }}
        />
      )}
      {overflowing && (
        <Flex flexDirection="row" justifyContent="center" style={{ marginTop: 4 }}>
          <Button
            size="sm"
            appearance="subtle"
            icon={expanded ? <IconChevronUp /> : <IconChevronDown />}
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        </Flex>
      )}
    </Box>
  );
}
